import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Calendar, Clock, MapPin, Mail, Phone, FileText, Search, Filter, Check, X, Loader2, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProQuotesPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [dateRange, setDateRange] = useState('all');
  
  // Modals
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [packageSelectModalOpen, setPackageSelectModalOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    fetchData();
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    applyFilters();
  }, [quotes, searchTerm, statusFilter, serviceFilter, sortBy, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/quotes`),
        axios.get(`${API_URL}/api/categories`)
      ]);
      setQuotes(quotesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch quote requests.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...quotes];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    
    // Service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(q => q.service === serviceFilter);
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(q => {
        const quoteDate = new Date(q.createdAt);
        const daysDiff = Math.floor((now - quoteDate) / (1000 * 60 * 60 * 24));
        
        switch(dateRange) {
          case 'today': return daysDiff === 0;
          case 'week': return daysDiff <= 7;
          case 'month': return daysDiff <= 30;
          default: return true;
        }
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'service':
          return a.service.localeCompare(b.service);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
    
    setFilteredQuotes(filtered);
  };

  const handleAcceptReject = async () => {
    if (!selectedQuote || !actionType) return;
    
    setActionLoading(selectedQuote.id);
    try {
      await axios.patch(`${API_URL}/api/quotes/${selectedQuote.id}/status`, {
        status: actionType
      });
      
      setQuotes(quotes.map(q => 
        q.id === selectedQuote.id ? { ...q, status: actionType } : q
      ));
      
      toast({
        title: actionType === 'accepted' ? '✅ Lead Accepted!' : 'Lead Rejected',
        description: actionType === 'accepted' 
          ? 'Lead added to your invoice. You have 20 minutes to pay.' 
          : 'The quote request has been rejected.'
      });
      
      setConfirmModalOpen(false);
      setDetailsModalOpen(false);
      
      // Refresh page after accepting to update checkout badge
      if (actionType === 'accepted') {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to update quote status.',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelLead = async () => {
    if (!selectedQuote) return;
    
    setActionLoading(selectedQuote.id);
    try {
      await axios.post(`${API_URL}/api/quotes/${selectedQuote.id}/cancel-lead`);
      
      toast({
        title: 'Lead Cancelled',
        description: 'The lead has been returned to the general queue.'
      });
      
      setCancelModalOpen(false);
      
      // Refresh the page to update the list
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error cancelling lead:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel lead.',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };
  
  const openCancelModal = (quote) => {
    // Check if within 20 minutes
    const acceptedAt = new Date(quote.acceptedAt + 'Z');
    const now = new Date();
    const minutesElapsed = (now - acceptedAt) / 1000 / 60;
    
    if (minutesElapsed > 20) {
      toast({
        title: 'Cannot Cancel',
        description: 'Lead can only be cancelled within 20 minutes of acceptance.',
        variant: 'destructive'
      });
      return;
    }
    
    setSelectedQuote(quote);
    setCancelModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const analytics = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    accepted: quotes.filter(q => q.status === 'accepted').length,
    rejected: quotes.filter(q => q.status === 'rejected').length,
    conversionRate: quotes.length > 0 ? Math.round((quotes.filter(q => q.status === 'accepted').length / quotes.length) * 100) : 0
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Show professional-only message for non-professionals
  if (user?.userType !== 'professional') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Account Required</h2>
          <p className="text-gray-600 mb-6">Lead management is only available for professional accounts.</p>
          <Button onClick={() => navigate('/')} className="bg-orange-600 hover:bg-orange-700 text-white">
            Back to Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Lead Management</h1>
          <p className="text-gray-600 mt-2">Manage and respond to quote requests from customers</p>
        </div>

        {/* Analytics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{analytics.total}</p>
              <p className="text-sm text-gray-600">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{analytics.pending}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{analytics.accepted}</p>
              <p className="text-sm text-gray-600">Accepted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{analytics.rejected}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{analytics.conversionRate}%</p>
              <p className="text-sm text-gray-600">Conversion</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by service, name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
              
              {/* Service Filter */}
              <select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Services</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="service">By Service</option>
                <option value="status">By Status</option>
              </select>
            </div>
            
            {/* Date Range Filter */}
            <div className="flex gap-2 mt-4">
              <span className="text-sm font-medium text-gray-700 flex items-center">Date Range:</span>
              {['all', 'today', 'week', 'month'].map(range => (
                <Button
                  key={range}
                  size="sm"
                  variant={dateRange === range ? 'default' : 'outline'}
                  onClick={() => setDateRange(range)}
                  className={dateRange === range ? 'bg-orange-600 hover:bg-orange-700' : ''}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quote Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
              <p className="text-gray-600">Try adjusting your filters or check back later for new quote requests.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredQuotes.map(quote => {
              // Debug: Log button rendering conditions
              const showCancelButton = quote.status === 'accepted' && quote.leadPaymentStatus === 'unpaid';
              if (showCancelButton) {
                console.log('Rendering Cancel button for quote:', quote.id, 'status:', quote.status, 'leadPaymentStatus:', quote.leadPaymentStatus);
              }
              
              return (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{quote.service}</h3>
                        <Badge className={getStatusColor(quote.status)}>
                          {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {quote.status === 'accepted' && quote.leadPaymentStatus === 'paid' ? quote.email : '•••••@••••••'}
                        </div>
                        <div className="flex items-center">
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {new Date(quote.createdAt).toLocaleDateString()}
                        </div>
                        {quote.preferredDate && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                            Preferred: {quote.preferredDate}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-gray-700 line-clamp-2">{quote.description}</p>
                    </div>
                    
                    <div className="flex flex-col gap-2 md:ml-6">
                      <Button
                        onClick={() => {
                          setSelectedQuote(quote);
                          setDetailsModalOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        View Details
                      </Button>
                      
                      {quote.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => {
                              setSelectedQuote(quote);
                              setActionType('accepted');
                              setConfirmModalOpen(true);
                            }}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedQuote(quote);
                              setActionType('rejected');
                              setConfirmModalOpen(true);
                            }}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      
                      {quote.status === 'accepted' && quote.leadPaymentStatus === 'unpaid' && (
                        <Button
                          onClick={() => openCancelModal(quote)}
                          size="sm"
                          variant="outline"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          disabled={actionLoading === quote.id}
                        >
                          {actionLoading === quote.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Cancel Lead
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-xl font-bold text-gray-900">{selectedQuote.service}</h3>
                <Badge className={getStatusColor(selectedQuote.status)}>
                  {selectedQuote.status.charAt(0).toUpperCase() + selectedQuote.status.slice(1)}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Contact Name</Label>
                  <p className="text-gray-900">{selectedQuote.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Email</Label>
                  {selectedQuote.status === 'accepted' && selectedQuote.leadPaymentStatus === 'paid' ? (
                    <p className="text-gray-900">{selectedQuote.email}</p>
                  ) : selectedQuote.status === 'accepted' && selectedQuote.leadPaymentStatus === 'unpaid' ? (
                    <p className="text-orange-600 italic font-medium">Pay invoice to view email</p>
                  ) : (
                    <p className="text-gray-500 italic">Accept lead to view email</p>
                  )}
                </div>
                <div>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Requested Date</Label>
                  <p className="text-gray-900">{new Date(selectedQuote.createdAt).toLocaleDateString()}</p>
                </div>
                {selectedQuote.preferredDate && (
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-700">Preferred Service Date</Label>
                    <p className="text-gray-900">{selectedQuote.preferredDate}</p>
                  </div>
                )}
              </div>
              
              {selectedQuote.status === 'pending' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Contact information will be revealed after you accept and pay for this lead. You'll have 20 minutes to pay before the lead returns to the queue.
                  </p>
                </div>
              )}
              
              {selectedQuote.status === 'accepted' && selectedQuote.leadPaymentStatus === 'unpaid' && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <p className="text-sm text-orange-800">
                    <strong>Payment Required:</strong> This lead has been accepted but not yet paid for. Go to your Pending Invoice to complete payment and access customer contact information.
                  </p>
                  <Button 
                    onClick={() => navigate('/pending-invoice')}
                    className="mt-3 bg-orange-600 hover:bg-orange-700"
                  >
                    Pay Now
                  </Button>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <p className="text-gray-900 mt-1 p-3 bg-gray-50 rounded-md">{selectedQuote.description}</p>
              </div>
              
              {selectedQuote.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => {
                      setActionType('accepted');
                      setConfirmModalOpen(true);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Accept Lead
                  </Button>
                  <Button
                    onClick={() => {
                      setActionType('rejected');
                      setConfirmModalOpen(true);
                    }}
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject Lead
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accepted' ? 'Accept Lead' : 'Reject Lead'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              {actionType === 'accepted' 
                ? 'Are you sure you want to accept this lead? The customer will be notified and expects you to contact them.'
                : 'Are you sure you want to reject this lead? This action cannot be undone.'}
            </p>
            
            {selectedQuote && (
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-medium text-gray-900">{selectedQuote.service}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedQuote.name}
                  {selectedQuote.status === 'accepted' && ` - ${selectedQuote.email}`}
                </p>
                <p className="text-sm text-gray-500 mt-2">{selectedQuote.description}</p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setConfirmModalOpen(false)}
                variant="outline"
                className="flex-1"
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAcceptReject}
                className={`flex-1 ${actionType === 'accepted' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  actionType === 'accepted' ? 'Yes, Accept Lead' : 'Yes, Reject Lead'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Lead Confirmation Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600">Cancel Accepted Lead</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to cancel this lead? The lead will be returned to the general queue and become available for other professionals.
            </p>
            
            {selectedQuote && (
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                <p className="font-medium text-gray-900">{selectedQuote.service}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedQuote.name}</p>
                <p className="text-sm text-orange-600 mt-2 font-medium">
                  ⚠️ Your quota will be restored (you'll get this lead slot back)
                </p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setCancelModalOpen(false)}
                variant="outline"
                className="flex-1"
                disabled={actionLoading}
              >
                Keep Lead
              </Button>
              <Button
                onClick={handleCancelLead}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Lead'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ProQuotesPage;